import { TRADE_EVENTS } from "@/events/trade.events";
import { io, Socket } from "socket.io-client";

type SendMessagePayload = {
  tradeId: string;
  message?: string;
  imageUrl?: string;
};

type JoinResult = {
  success?: boolean;
  tradeId?: string;
  message?: string;
};

class TradeSocket {
  private socket: Socket | null = null;

  /**
   * Trade rooms that this client currently wants to be in.
   *
   * These are automatically rejoined after reconnecting.
   */
  private joinedRooms = new Set<string>();

  /**
   * Prevent multiple connect calls from creating
   * multiple connection attempts.
   */
  private connectPromise: Promise<Socket> | null = null;

  /**
   * Promise resolvers waiting for the socket to connect.
   */
  private connectionWaiters: Array<{
    resolve: (socket: Socket) => void;
    reject: (error: Error) => void;
  }> = [];

  connect(): Socket {
    if (this.socket) {
      return this.socket;
    }

    const url = import.meta.env.VITE_WS_URL;

    if (!url) {
      throw new Error("VITE_WS_URL is not configured");
    }

    console.log("🔌 Creating trade socket:", url);

    this.socket = io(url, {
      withCredentials: true,

      transports: ["websocket", "polling"],

      reconnection: true,
      reconnectionAttempts: Infinity,

      // Don't hammer the server during an outage.
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,

      // Don't give up on the initial connection too quickly.
      timeout: 20000,

      autoConnect: true,
    });

    this.setupListeners();

    return this.socket;
  }

  private setupListeners() {
    if (!this.socket) return;

    this.socket.on("connect", () => {
      console.log("✅ Trade socket connected:", this.socket?.id);

      /**
       * Resolve anything waiting for connection.
       */
      this.resolveConnectionWaiters();

      /**
       * IMPORTANT:
       * Every trade room is rejoined after reconnect.
       */
      this.rejoinRooms();
    });

    this.socket.on("disconnect", (reason) => {
      console.warn("⚠️ Trade socket disconnected:", reason);
    });

    this.socket.on("connect_error", (error) => {
      console.error("❌ Trade socket connection error:", error.message);
    });

    this.socket.io.on("reconnect_attempt", (attempt) => {
      console.log(`🔄 Trade socket reconnect attempt #${attempt}`);
    });

    this.socket.io.on("reconnect", (attempt) => {
      console.log(`✅ Trade socket reconnected after ${attempt} attempt(s)`);
    });

    this.socket.io.on("reconnect_error", (error) => {
      console.error("❌ Trade socket reconnect error:", error.message);
    });

    this.socket.io.on("reconnect_failed", () => {
      console.error("❌ Trade socket failed to reconnect");
    });

    this.socket.on(TRADE_EVENTS.SERVER.JOINED, (data) => {
      console.log("✅ Joined trade room:", data?.tradeId);
    });

    this.socket.on("error", (error) => {
      console.error("❌ Trade socket error:", error);
    });
  }

  /**
   * Wait until Socket.IO is connected.
   */
  async ensureConnected(): Promise<Socket> {
    const socket = this.connect();

    if (socket.connected) {
      return socket;
    }

    if (!this.connectPromise) {
      this.connectPromise = new Promise<Socket>((resolve, reject) => {
        this.connectionWaiters.push({
          resolve,
          reject,
        });

        /**
         * Explicitly trigger connection if necessary.
         */
        if (!socket.active) {
          socket.connect();
        }
      }).finally(() => {
        this.connectPromise = null;
      });
    }

    return this.connectPromise;
  }

  private resolveConnectionWaiters() {
    if (!this.socket?.connected) return;

    const socket = this.socket;

    const waiters = [...this.connectionWaiters];

    this.connectionWaiters = [];

    waiters.forEach(({ resolve }) => {
      resolve(socket);
    });
  }

  /**
   * Join a trade room.
   *
   * IMPORTANT:
   * The room remains in joinedRooms while TradeView
   * is actively viewing the trade.
   */
  async joinTrade(tradeId: string): Promise<void> {
    if (!tradeId) return;

    const socket = await this.ensureConnected();

    this.joinedRooms.add(tradeId);

    if (!socket.connected) {
      return;
    }

    await this.emitWithAck(TRADE_EVENTS.CLIENT.JOIN, { tradeId });

    console.log("📡 Trade room joined:", tradeId);
  }

  /**
   * Rejoin every active trade room after reconnect.
   */
  private async rejoinRooms() {
    if (!this.socket?.connected) return;

    const rooms = Array.from(this.joinedRooms);

    for (const tradeId of rooms) {
      try {
        await this.emitWithAck(TRADE_EVENTS.CLIENT.JOIN, { tradeId });

        console.log("🔄 Rejoined trade room:", tradeId);
      } catch (error) {
        console.error(`❌ Failed to rejoin trade ${tradeId}`, error);
      }
    }
  }

  /**
   * Leave a trade room when TradeView is actually unmounted.
   */
  async leaveTrade(tradeId: string) {
    if (!tradeId) return;

    this.joinedRooms.delete(tradeId);

    if (!this.socket?.connected) {
      return;
    }

    try {
      await this.emitWithAck(TRADE_EVENTS.CLIENT.LEAVE, { tradeId });

      console.log("🚪 Left trade room:", tradeId);
    } catch (error) {
      console.error("❌ Failed to leave trade room:", error);
    }
  }

  /**
   * Send a message reliably.
   *
   * If disconnected, wait for reconnection first.
   */
  async sendMessage(dto: SendMessagePayload): Promise<any> {
    const socket = await this.ensureConnected();

    /**
     * Make sure the user is in the trade room before
     * attempting to send.
     */
    if (!this.joinedRooms.has(dto.tradeId)) {
      await this.joinTrade(dto.tradeId);
    }

    /**
     * If reconnect happened while joining,
     * make sure we're connected.
     */
    if (!socket.connected) {
      throw new Error(
        "Trade connection is temporarily unavailable. Please try again.",
      );
    }

    return this.emitWithAck(TRADE_EVENTS.CLIENT.SEND_MESSAGE, dto);
  }

  startTyping(tradeId: string) {
    if (!this.socket?.connected) return;

    this.socket.emit(TRADE_EVENTS.CLIENT.TYPING_START, { tradeId });
  }

  stopTyping(tradeId: string) {
    if (!this.socket?.connected) return;

    this.socket.emit(TRADE_EVENTS.CLIENT.TYPING_STOP, { tradeId });
  }

  getSocket() {
    return this.socket;
  }

  isConnected() {
    return this.socket?.connected ?? false;
  }

  /**
   * Generic Socket.IO acknowledgement helper.
   */
  private emitWithAck<T = any>(event: string, payload: unknown): Promise<T> {
    return new Promise((resolve, reject) => {
      const socket = this.socket;

      if (!socket?.connected) {
        reject(new Error("Socket is not connected"));
        return;
      }

      socket
        .timeout(10000)
        .emit(event, payload, (error: Error | null, response: T) => {
          if (error) {
            reject(error);
            return;
          }

          resolve(response);
        });
    });
  }
}

export const tradeSocket = new TradeSocket();
